"use client";

import { useInterimStateManager } from "@/state/globalStore";
import LoadingState from "@/shared/ui/LoadingState";
import WithDrawerOpen from "@/shared/ui/gestures/WithDrawerOpen";
import { PROFILE_SHAPING_LABELS } from "@/features/profile-shaping/config/actions";
import { SituationSubmitForm } from "@/features/situations";
import {
  InsertThoughtsForm,
  SuggestedThoughtsReviewSurface,
  ThoughtTraceOrigin,
} from "@/features/thoughts";
import { SituationSourceShapingEntry } from "@/features/backlog";
import {
  BiasDetector,
  ReflectSingleThought,
  ReflectThoughts,
} from "@/features/reflect";
import { SituationConfig } from "@/features/settings";
import { MindstringPrototype, Situation } from "@/generated/fastapi";

/**
 * Centralized drawer management for Studio views
 * Renders all drawers and confirmation dialogs used across Studio views
 */
export default function StudioDrawers() {
  const { interimState, setInterimState, resetInterimState } =
    useInterimStateManager();
  const suggestedThoughtsReviewValue =
    interimState.suggested_thoughts_review?.value;

  return (
    <>
      {/* Edit situations */}
      <WithDrawerOpen
        title={PROFILE_SHAPING_LABELS.editSituation}
        isOpen={interimState.edit_situation?.isOpen ?? false}
        onOpenChangeAction={(isOpen) => {
          setInterimState("edit_situation", {
            isOpen,
            passedObject: interimState.edit_situation?.passedObject,
          });
        }}
        onCloseAction={() => {
          resetInterimState("edit_situation");
        }}
      >
        <SituationSubmitForm
          situationToEdit={
            interimState.edit_situation?.passedObject as Situation
          }
        />
      </WithDrawerOpen>

      {/* Suggested thoughts review */}
      <WithDrawerOpen
        title="Review suggestions"
        isOpen={interimState.suggested_thoughts_review?.isOpen ?? false}
        onOpenChangeAction={(isOpen) => {
          setInterimState("suggested_thoughts_review", {
            isOpen,
            value: interimState.suggested_thoughts_review?.value,
          });
        }}
        onCloseAction={() => {
          resetInterimState("suggested_thoughts_review");
        }}
      >
        {suggestedThoughtsReviewValue ? (
          <SuggestedThoughtsReviewSurface
            prototype={suggestedThoughtsReviewValue.prototype}
            situation={suggestedThoughtsReviewValue.situation}
            showPrototypeSelector={!suggestedThoughtsReviewValue.situation}
            showSituationSelector={!suggestedThoughtsReviewValue.situation}
            scopeItems={
              suggestedThoughtsReviewValue.situation
                ? [
                    {
                      label:
                        suggestedThoughtsReviewValue.situation.abbr ||
                        suggestedThoughtsReviewValue.situation.position ||
                        "Situation",
                      colorPalette: "teal",
                    },
                  ]
                : undefined
            }
          />
        ) : null}
      </WithDrawerOpen>

      {/* Reflect Thought */}
      <WithDrawerOpen
        title="Reflect thought"
        isOpen={interimState.reflect_thought?.isOpen ?? false}
        onOpenChangeAction={(isOpen) => {
          setInterimState("reflect_thought", {
            isOpen,
            passedObject: interimState.reflect_thought?.passedObject,
          });
        }}
        onCloseAction={() => {
          resetInterimState("reflect_thought");
        }}
      >
        <ReflectSingleThought
          thought={
            interimState.reflect_thought?.passedObject as {
              objectID: string;
            }
          }
        />
      </WithDrawerOpen>

      {/* Source material */}
      <WithDrawerOpen
        title="Source material"
        isOpen={interimState.trace_origin?.isOpen ?? false}
        onOpenChangeAction={(isOpen) => {
          setInterimState("trace_origin", {
            isOpen,
            passedObject: interimState.trace_origin?.passedObject,
          });
        }}
        onCloseAction={() => {
          resetInterimState("trace_origin");
        }}
      >
        <ThoughtTraceOrigin
          originId={interimState.trace_origin?.passedObject?.originId}
        />
      </WithDrawerOpen>

      {/* Add Thoughts */}
      <WithDrawerOpen
        title={PROFILE_SHAPING_LABELS.flow}
        isOpen={interimState.add_thoughts?.isOpen ?? false}
        onOpenChangeAction={(isOpen) => {
          setInterimState("add_thoughts", {
            isOpen,
            value: interimState.add_thoughts?.value,
            prototypeCategory: interimState.add_thoughts?.prototypeCategory,
          });
        }}
        onCloseAction={() => {
          resetInterimState("add_thoughts");
        }}
      >
        <InsertThoughtsForm
          situationId={(interimState.add_thoughts?.value as string) ?? ""}
          prototype={
            (interimState.add_thoughts
              ?.prototypeCategory as MindstringPrototype) ?? "activity"
          }
        />
      </WithDrawerOpen>

      {/* Extract Thoughts */}
      <WithDrawerOpen
        title={PROFILE_SHAPING_LABELS.flow}
        isOpen={interimState.extract_thoughts?.isOpen ?? false}
        onOpenChangeAction={(isOpen) => {
          setInterimState("extract_thoughts", {
            isOpen,
            value: interimState.extract_thoughts?.value,
            prototypeCategory: interimState.extract_thoughts?.prototypeCategory,
          });
        }}
        onCloseAction={() => {
          resetInterimState("extract_thoughts");
        }}
      >
        <SituationSourceShapingEntry
          situationId={interimState.extract_thoughts?.value as string}
          prototype={
            interimState.extract_thoughts
              ?.prototypeCategory as MindstringPrototype
          }
        />
      </WithDrawerOpen>

      {/* Bias Detector */}
      <WithDrawerOpen
        title="Bias detector"
        isOpen={interimState.insight_bias?.isOpen ?? false}
        onOpenChangeAction={(isOpen) => {
          setInterimState("insight_bias", {
            isOpen,
            value: interimState.insight_bias?.value,
            prototypeCategory: interimState.insight_bias?.prototypeCategory,
          });
        }}
        onCloseAction={() => {
          resetInterimState("insight_bias");
        }}
      >
        <BiasDetector
          situationId={interimState.insight_bias?.value as string}
          prototype={
            interimState.insight_bias?.prototypeCategory as MindstringPrototype
          }
        />
      </WithDrawerOpen>

      {/* Situation Config */}
      <WithDrawerOpen
        title="Profile export settings"
        isOpen={interimState.batch_situation_config?.isOpen ?? false}
        onOpenChangeAction={(isOpen) => {
          setInterimState("batch_situation_config", {
            isOpen,
            value: interimState.batch_situation_config?.value,
          });
        }}
        onCloseAction={() => {
          resetInterimState("batch_situation_config");
        }}
      >
        {interimState.batch_situation_config?.value ? (
          <SituationConfig
            mode="batch"
            situationIds={
              interimState.batch_situation_config.value.situationIds
            }
            prototype={interimState.batch_situation_config.value.prototype}
            onCancel={() => resetInterimState("batch_situation_config")}
          />
        ) : (
          <LoadingState
            loadingMessage="Loading profile export settings"
            message="Preparing profile export settings for selected situations."
          />
        )}
      </WithDrawerOpen>

      {/* Reflect thoughts */}
      <WithDrawerOpen
        title="Reflect thoughts"
        isOpen={interimState.reflect_thoughts?.isOpen ?? false}
        onOpenChangeAction={(isOpen) => {
          setInterimState("reflect_thoughts", {
            isOpen,
            value: interimState.reflect_thoughts?.value,
            prototypeCategory: interimState.reflect_thoughts?.prototypeCategory,
          });
        }}
        onCloseAction={() => {
          resetInterimState("reflect_thoughts");
        }}
      >
        <ReflectThoughts
          situationId={interimState.reflect_thoughts?.value as string | null}
          prototype={
            interimState.reflect_thoughts
              ?.prototypeCategory as MindstringPrototype
          }
          limit={10}
        />
      </WithDrawerOpen>
    </>
  );
}
